/* global G */
/* eslint-disable no-restricted-globals */
import { useCallback, useContext, useEffect, useRef, useState, useTransition } from 'react'
import ApplicationContext from '@platform/react/context/application'
import useEventHandler from '@platform/react/hook/useEventHandler'
import useEventCallback from '@platform/react/hook/useEventCallback'

export interface AttachmentValue {
  name?: string
  type?: string
  mimetype?: string
  size?: number
}

export interface Attachment {
  key?: string | null
  uuid?: string
  api?: Record<string, unknown>
  value?: AttachmentValue
  url?: string
}

export interface AttachmentState {
  src: string | null
  progress: number
  loading: boolean
  placeholder: boolean
  status?: number
}

export interface UseAttachmentAPIProps {
  attachment?: Attachment
  initial?: boolean
  group?: string | null
}

export interface UseAttachmentAPIResult {
  state: AttachmentState
  onLoad: () => void
  fetch: () => void
  uploads: Attachment[]
}

const initialState: AttachmentState = {
  progress: 0,
  loading: true,
  placeholder: false,
  src: null,
}

/**
 * Fetches the attachments of `group` from the attachment adapter.
 *
 * @param group    attachment group
 * @param eventBus event adapter
 * @returns        the attachments of the group
 */
const _getGroupAttachments = async (group: string, eventBus: any): Promise<any[]> => new Promise(
  (resolve) => {
    const doneEventName = eventBus.type(G.ATTACHMENT, G.DONE, group)
    const doneEventHandler = ({ detail }: any) => {
      eventBus.remove(doneEventName, doneEventHandler)
      resolve(detail[G.DATA] || [])
    }
    eventBus.add(doneEventName, doneEventHandler)
    eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.CACHE, group), { [G.DATA]: {} })
    eventBus.remove(doneEventName, doneEventHandler)
    // if the group we call `G.CACHE` on doesn't exist, we'll wait here forever.
    // instead, resolve with an empty list, this will trigger the caller to re-fetch
    // the attachments
    resolve([])
  },
)

/**
 * useAttachmentAPI Hook
 *
 * Automates the usage of the attachment adapter: fetches `attachment` (right
 * away, or once `fetch()` is called), reports download progress and keeps
 * track of already fetched attachments. Also exposes the attachment adapter's
 * in-flight `uploads`.
 *
 * @param props            the hook's properties
 * @param props.attachment the attachment to fetch
 * @param props.initial    whether to fetch the attachment right away
 * @param props.group      attachment group identifier
 * @returns                the current {@link AttachmentState}, its handlers and the `uploads` list
 */
const useAttachmentAPI = ({
  attachment, group = null, initial = true,
}: UseAttachmentAPIProps = {}): UseAttachmentAPIResult => {
  const { eventBus, attachment: attachmentAdapter } = useContext(ApplicationContext) as any
  const [id, setId] = useState(attachment?.key || null)
  const [uploads, setUploads] = useState<Attachment[]>(
    () => (attachmentAdapter?.[G.STATE]?.uploads || []).map((entry: any) => ({ ...entry })),
  )
  const [read, setRead] = useState(initial)
  const [, startTransition] = useTransition()
  const [state, setState] = useState(initialState)
  const { loading } = state

  // List of already fetched attachments. A ref is used instead of state, as
  // the residual object urls need to be revoked once the component unmounts,
  // at which point a state would already have been cleared, leaking memory.
  const loadedAttachments = useRef<any[]>([])
  const { key, uuid = key } = attachment || {}

  // If initial changes from the outside, set read accordingly
  useEffect(() => { setRead(initial) }, [initial])

  // One instance of this hook may maintain multiple attachments over time,
  // meaning `attachment` may change during runtime. `id` indicates the
  // attachment currently displayed and only changes if the incoming
  // attachment did.
  useEffect(() => {
    const newId = attachment?.key || null
    id !== newId && startTransition(() => {
      setId(newId)
      setState(initialState)
      setRead(initial)
    })
  }, [attachment])

  useEffect(() => {
    if (!read && !loading) return

    if (read) {
      (async () => {
        const groupAttachments = group ? await _getGroupAttachments(group, eventBus) : []
        const alreadyLoadedAttachment = loadedAttachments.current.find(x => x.key === id) || null
        const alreadyLoadedGroupAttachment = groupAttachments
          .find(x => x.key === id && x.url) || null

        if (alreadyLoadedGroupAttachment) {
          setState(prevState => ({
            ...prevState,
            src: alreadyLoadedGroupAttachment.url || prevState.src,
            loading: false,
            placeholder: false,
          }))
        } else if (alreadyLoadedAttachment) {
          startTransition(() => setState(alreadyLoadedAttachment))
        } else {
          attachment?.value?.name && eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.READ), {
            [G.DATA]: group ? { ...attachment, group } : attachment,
          })
        }
      })()
    }
  }, [read, id])

  // Revoking all object urls when the component unmounts. Attachments that
  // are part of a group are managed by the adapter instead.
  useEffect(() => () => loadedAttachments.current.forEach((x) => {
    !group && URL.revokeObjectURL?.(x.src)
  }), [])

  const _progressEventName = eventBus.type(G.ATTACHMENT, G.READ, key)
  const _progressEventHandler = useEventCallback(({ detail }: any) => {
    const { progress, status } = detail
    detail.uuid === uuid && setState(prevState => ({
      ...prevState,
      loading: progress !== 100 && status === 200,
      progress,
    }))
  })
  useEventHandler(eventBus, _progressEventName, _progressEventHandler)

  const _doneEventName = eventBus.type(G.ATTACHMENT, G.DONE, key)
  const _doneEventHandler = useEventCallback(({ detail }: any) => {
    const { url, status } = detail
    detail.uuid === uuid && setState((prevState) => {
      const newState = {
        ...prevState,
        src: url || prevState.src,
        placeholder: false,
        loading: false,
        status,
      }

      // Only locally manage the attachments if there is no group, otherwise
      // `revokeObjectURL` may be called on an attachment that's part of one
      !group && (loadedAttachments.current = [
        ...loadedAttachments.current,
        { ...newState, key: detail.key },
      ])

      return newState
    })
  })
  useEventHandler(eventBus, _doneEventName, _doneEventHandler)

  const _uploadsHandler = useEventCallback(({ detail }: any) => {
    setUploads((detail?.[G.DATA] || []).map((entry: any) => ({ ...entry })))
  })
  useEventHandler(eventBus, eventBus.type(G.ATTACHMENT, G.PROGRESS), _uploadsHandler)

  const onLoad = useCallback(() => {
    eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.CACHE, key))
  }, [])

  const fetch = useCallback(() => { setRead(true) }, [])

  return { state, onLoad, fetch, uploads }
}

export default useAttachmentAPI
